import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { tutorProfile } from '@/mocks/tutor';

interface TutorSidebarProps {
  activeSection: string;
  onSelect: (section: string) => void;
}

const navItems = [
  { id: 'overview', label: 'Overview', icon: 'ri-dashboard-line' },
  { id: 'sessions', label: 'My Sessions', icon: 'ri-video-line' },
  { id: 'students', label: 'My Students', icon: 'ri-user-line' },
  { id: 'resources', label: 'Resources', icon: 'ri-folder-open-line' },
  { id: 'messages', label: 'Messages', icon: 'ri-message-3-line', badge: 2 },
  { id: 'profile', label: 'My Profile', icon: 'ri-user-settings-line' },
];

export default function TutorSidebar({ activeSection, onSelect }: TutorSidebarProps) {
  const [user, setUser] = useState<{ name: string; email: string; avatar?: string } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const raw = localStorage.getItem('user');
      setUser(raw ? JSON.parse(raw) : null);
    } catch {
      setUser(null);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  return (
    <aside className="w-60 flex-shrink-0 bg-slate-900 min-h-screen flex flex-col">
      <div className="p-5 border-b border-slate-800">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-600 rounded-lg">
            <i className="ri-computer-line text-white text-base"></i>
          </div>
          <span className="font-bold text-sm text-white" style={{ fontFamily: "'Inter', sans-serif" }}>
            CS<span className="text-emerald-400">Revision</span>Hub
          </span>
        </Link>
        <div className="mt-3 flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center bg-emerald-700/30 rounded-md">
            <i className="ri-user-star-line text-emerald-400 text-xs"></i>
          </div>
          <span className="text-xs text-emerald-400 font-semibold uppercase tracking-wider">Tutor Portal</span>
        </div>
      </div>

      <nav className="flex-1 p-3 flex flex-col gap-0.5">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => onSelect(item.id)}
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer text-left ${
              activeSection === item.id
                ? 'bg-emerald-700 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <span className="flex items-center gap-2.5">
              <i className={`${item.icon} text-base`}></i>
              {item.label}
            </span>
            {item.badge && (
              <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                {item.badge}
              </span>
            )}
          </button>
        ))}
      </nav>

      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 bg-slate-700 flex items-center justify-center text-white font-semibold">
            {user?.avatar ? (
              <img src={user.avatar} alt="" className="w-full h-full object-cover object-top" />
            ) : (
              user?.name ? user.name.slice(0, 2).toUpperCase() : 'T'
            )}
          </div>
          <div className="min-w-0">
            <p className="text-white text-xs font-semibold truncate">{user?.name || tutorProfile.name}</p>
            <p className="text-slate-500 text-xs truncate">{user?.email || tutorProfile.boards.join(', ')}</p>
          </div>
        </div>
        <button onClick={handleLogout} className="w-full flex items-center gap-2 text-slate-500 hover:text-red-400 text-xs transition-colors cursor-pointer bg-transparent border-none p-0 text-left">
          <i className="ri-logout-box-line"></i> Sign Out
        </button>
      </div>
    </aside>
  );
}
